import { createReducer } from '@reduxjs/toolkit';
import {
  filterTransactions,
  searchTransactions,
  exportTransactions,
  listTransactions,
  getTransactionDetails,
  reportTransaction,
} from '../actions/transaction';

const initialState = {
  transactions: {
    data: [],            // List of transactions (includes filtered or search results)
    currentPage: 0,      // Current page for pagination
    totalRecords: 0,       // Total number of pages
    pageSize: 0,        // Items per page
  },
  loading: false,        // Loading state for listing, filtering, or searching transactions
  error: null,           // General error state

  // Export state as an object
  export: {
    loading: false,      // Loading state for export action
    success: false,      // Tracks if export was successful
    error: null,         // Export-specific error
  },

  
  // Export state as an object
  txReported: {
    loading: false,
    success: false, 
    error: null,  
  },

  // State for getting transaction details
  getTransactionDetails: {
    data: null,          // Transaction details data
    loading: false,
    error: null,
  },
};

const transactionsReducer = createReducer(initialState, (builder) => {
  builder
  // Filter transactions
  .addCase(String(filterTransactions.trigger), (state) => {
    state.loading = true;
    state.error = null;
  })
  .addCase(String(filterTransactions.resolved), (state, action) => {
    const { transactions, currentPage, totalRecords, pageSize } = action.payload;
    state.loading = false;
    state.transactions = {
      data: transactions,    // Set filtered transactions data
      currentPage,           // Update current page from the backend
      totalRecords,            // Update total pages from the backend
      pageSize,              // Update page size (if provided by backend)
    };
  })
  .addCase(String(filterTransactions.rejected), (state, action) => {
    state.loading = false;
    state.error = action.payload; // Handle filtering error
  })

  // Search transactions
  .addCase(String(searchTransactions.trigger), (state) => {
    state.loading = true;
    state.error = null;
  })
  .addCase(String(searchTransactions.resolved), (state, action) => {
    const { transactions, currentPage, totalRecords, pageSize } = action.payload;
    state.loading = false;
    state.transactions = {
      data: transactions,    // Set search results
      currentPage,           // Update pagination after search
      totalRecords,
      pageSize,
    };
  })
  .addCase(String(searchTransactions.rejected), (state, action) => {
    state.loading = false;
    state.error = action.payload; // Handle search error
  })

  // Export transactions
  .addCase(String(exportTransactions.trigger), (state) => {
    state.export.loading = true;  // Set export-specific loading
    state.export.success = false; // Reset success status
    state.export.error = null;    // Clear export errors
  })
  .addCase(String(exportTransactions.resolved), (state) => {
    state.export.loading = false; // Stop export loading
    state.export.success = true;  // Set export success flag
  })
  .addCase(String(exportTransactions.rejected), (state, action) => {
    state.export.loading = false; // Stop export loading
    state.export.success = false; // Mark export as unsuccessful
    state.export.error = action.payload; // Handle export error
  })

  // Report transaction
  .addCase(String(reportTransaction.trigger), (state) => {
    state.txReported.loading = true; 
    state.txReported.success = false; 
    state.txReported.error = null;  
  })
  .addCase(String(reportTransaction.resolved), (state) => {
    state.txReported.loading = false;
    state.txReported.success = true; 
  })
  .addCase(String(reportTransaction.rejected), (state, action) => {
    state.txReported.loading = false;
    state.txReported.success = false; 
    state.txReported.error = action.payload;
  })

  // List transactions
  .addCase(String(listTransactions.trigger), (state) => {
    state.loading = true;
    state.error = null;
  })
  .addCase(String(listTransactions.resolved), (state, action) => {

    const { 
      data: transactions, 
      current_page: currentPage, 
      next_page_url: nextPageUrl, 
      prev_page_url: prevPageUrl, 
      total: totalRecords, 
      per_page: pageSize 
    } = action.payload;

    state.loading = false;
    state.transactions = {
      data: transactions,    // Set list of transactions
      currentPage,           // Update pagination after listing
      totalRecords,
      pageSize,
      nextPageUrl,
      prevPageUrl
    };
  })
  .addCase(String(listTransactions.rejected), (state, action) => {
    state.loading = false;
    state.error = action.payload; // Handle listing error
  })

  // Get transaction details
  .addCase(String(getTransactionDetails.trigger), (state) => {
    state.getTransactionDetails.loading = true;
    state.getTransactionDetails.error = null;
  })
  .addCase(String(getTransactionDetails.resolved), (state, action) => {
    state.getTransactionDetails.loading = false;
    state.getTransactionDetails.data = action.payload; // Set fetched transaction details
  })
  .addCase(String(getTransactionDetails.rejected), (state, action) => {
    state.getTransactionDetails.loading = false;
    state.getTransactionDetails.error = action.payload; // Handle error for getting transaction details
  })
});

export default transactionsReducer;
